import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { knowledgebaseService } from '../services/knowledgebaseService';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Search, 
  Link, 
  FileText, 
  File, 
  BookOpen, 
  Calendar,
  MoreHorizontal,
  Trash2,
  X
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Alert, AlertTitle, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import Dropzone from '../components/ui/dropzone';

const KnowledgeBase = () => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [addUrlDialogOpen, setAddUrlDialogOpen] = useState(false);
  const [addTextDialogOpen, setAddTextDialogOpen] = useState(false);
  const [addFilesDialogOpen, setAddFilesDialogOpen] = useState(false);
  const [documentName, setDocumentName] = useState('');
  const [documentUrl, setDocumentUrl] = useState('');
  const [documentText, setDocumentText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const navigate = useNavigate();

  // Fetch documents from API
  const fetchDocuments = async () => {
    try {
      setIsLoading(true);
      const response = await knowledgebaseService.getDocuments();
      const documentsArray = response?.documents || [];
      setDocuments(documentsArray);
    } catch (err) {
      setError(`Failed to load documents: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  // Filter documents based on search term
  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.type?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get document type icon and color
  const getDocumentTypeInfo = (type) => {
    switch (type?.toLowerCase()) {
      case 'url':
        return { icon: Link, color: 'text-chart-4', bgColor: 'bg-chart-4/10' };
      case 'file':
        return { icon: File, color: 'text-chart-1', bgColor: 'bg-chart-1/10' };
      case 'text':
        return { icon: FileText, color: 'text-chart-2', bgColor: 'bg-chart-2/10' };
      default:
        return { icon: BookOpen, color: 'text-muted-foreground', bgColor: 'bg-muted' };
    }
  };

  // Validation functions
  const validateUrlForm = () => {
    const errors = {};
    
    if (!documentName.trim()) {
      errors.documentName = 'Document name is required';
    }
    
    if (!documentUrl.trim()) {
      errors.documentUrl = 'URL is required';
    } else {
      // Basic URL validation
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(documentUrl.trim())) {
        errors.documentUrl = 'Please enter a valid URL (starting with http:// or https://)';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateTextForm = () => {
    const errors = {};
    
    if (!documentName.trim()) {
      errors.documentName = 'Document name is required';
    }
    
    if (!documentText.trim()) {
      errors.documentText = 'Text content is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateFileForm = () => {
    const errors = {};
    
    if (selectedFiles.length === 0) {
      errors.selectedFiles = 'Please select at least one file to upload';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle URL submission
  const handleAddUrl = async () => {
    if (!validateUrlForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await knowledgebaseService.createDocumentFromUrl({
        name: documentName.trim(),
        url: documentUrl.trim()
      });
      
      // Refresh documents list
      await fetchDocuments();
      
      // Reset form and close dialog
      setDocumentName('');
      setDocumentUrl('');
      setFormErrors({});
      setAddUrlDialogOpen(false);
      
      // Show success notification
      toast.success('URL document added successfully!');
    } catch (err) {
      console.error('Failed to add URL:', err);
      toast.error('Failed to add URL document. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Text submission
  const handleAddText = async () => {
    if (!validateTextForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await knowledgebaseService.createDocumentFromText({
        name: documentName.trim(),
        text: documentText.trim()
      });
      
      // Refresh documents list
      await fetchDocuments();
      
      // Reset form and close dialog
      setDocumentName('');
      setDocumentText('');
      setFormErrors({});
      setAddTextDialogOpen(false);
      
      // Show success notification
      toast.success('Text document created successfully!');
    } catch (err) {
      console.error('Failed to add text:', err);
      toast.error('Failed to create text document. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle File submission
  const handleAddFile = async () => {
    if (!validateFileForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Upload each file separately
      const uploadPromises = selectedFiles.map(async (file) => {
        const formData = new FormData();
        formData.append('name', file.name);
        formData.append('file', file);
        
        return knowledgebaseService.createDocumentFromFile(formData);
      });
      
      await Promise.all(uploadPromises);
      
      // Refresh documents list
      await fetchDocuments();
      
      // Reset form and close dialog
      setSelectedFiles([]);
      setFormErrors({});
      setAddFilesDialogOpen(false);
      
      // Show success notification
      toast.success(`${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} uploaded successfully!`);
    } catch (err) {
      console.error('Failed to add files:', err);
      toast.error('Failed to upload files. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle removing individual files
  const handleRemoveFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };


  // Handle document actions
  const handleViewDocument = (documentId) => {
    navigate(`/knowledge-base/${documentId}`);
  };

  const handleDeleteDocument = (document) => {
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteDocument = async () => {
    if (!documentToDelete) return;
    
    try {
      await knowledgebaseService.deleteDocument(documentToDelete.id);
      setDocuments(prev => prev.filter(doc => doc.id !== documentToDelete.id));
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      toast.success('Document deleted successfully!');
    } catch (err) {
      console.error('Failed to delete document:', err);
      toast.error('Failed to delete document. Please try again.');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-muted/50 p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold text-card-foreground mb-6">
            Knowledge Base
          </h1>
          <div className="bg-destructive/10 text-destructive p-6 rounded-lg border border-destructive/20">
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background rounded-tl-lg -mr-8">
      {/* Header with enhanced styling */}
      <div className="bg-background border-b border-border sticky top-0 z-10 rounded-tl-lg">
        <div className="flex h-16 items-center justify-between px-8">
          <div className="space-y-1">
            <h1 className="text-xl font-bold text-card-foreground tracking-tight">Knowledge Base</h1>
          </div>
        </div>
      </div>

      <div className="px-8 py-8 space-y-8 overflow-x-hidden">
        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card 
            className="border border-border bg-card shadow-none hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setAddUrlDialogOpen(true)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-card-foreground mb-1">Add URL</h3>
                  <p className="text-sm text-muted-foreground">Import content from a web page</p>
                </div>
                <div className="w-10 h-10 bg-chart-4/10 rounded-lg flex items-center justify-center">
                  <Link className="w-4 h-4 text-chart-4" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border border-border bg-card shadow-none hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setAddFilesDialogOpen(true)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-card-foreground mb-1">Add Files</h3>
                  <p className="text-sm text-muted-foreground">Upload documents and files</p>
                </div>
                <div className="w-10 h-10 bg-chart-1/10 rounded-lg flex items-center justify-center">
                  <File className="w-4 h-4 text-chart-1" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border border-border bg-card shadow-none hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setAddTextDialogOpen(true)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-card-foreground mb-1">Create Text</h3>
                  <p className="text-sm text-muted-foreground">Add custom text content</p>
                </div>
                <div className="w-10 h-10 bg-chart-2/10 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-chart-2" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search Bar */}
        <div className="flex justify-between items-center">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-input focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>

        {/* Documents Table */}
        <Card className="border border-border bg-card shadow-none">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border">
                  <TableHead className="text-muted-foreground font-semibold">Document</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Created</TableHead>
                  <TableHead className="text-muted-foreground font-semibold">Last Updated</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  // Skeleton loading rows
                  Array.from({ length: 5 }).map((_, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <Skeleton className="h-10 w-10" />
                          <div className="space-y-2">
                            <Skeleton className="h-4 w-[200px]" />
                            <Skeleton className="h-3 w-[150px]" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[80px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-4 w-[80px]" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="h-8 w-8 rounded" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredDocuments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="h-32 text-center">
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="w-16 h-16 bg-muted flex items-center justify-center mb-4">
                          <BookOpen className="w-8 h-8 text-muted-foreground" />
              </div>
                        <h3 className="text-xl font-bold text-card-foreground mb-2">
                          {searchTerm ? 'No documents found' : 'No documents yet'}
                        </h3>
              <p className="text-muted-foreground">
                          {searchTerm 
                            ? 'No documents match your search criteria.' 
                            : 'Get started by adding your first document to the knowledge base.'
                          }
              </p>
            </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredDocuments.map((document) => {
                    const typeInfo = getDocumentTypeInfo(document.type);
                    const TypeIcon = typeInfo.icon;
                    
                    return (
                      <TableRow
                        key={document.id}
                        className="cursor-pointer hover:bg-muted/50 transition-colors border-b border-border"
                        onClick={() => handleViewDocument(document.id)}
                      >
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 ${typeInfo.bgColor} rounded-lg flex items-center justify-center`}>
                              <TypeIcon className={`w-5 h-5 ${typeInfo.color}`} />
                            </div>
                            <div>
                              <div className="font-semibold text-card-foreground">{document.name || 'Untitled Document'}</div>
                              <div className="text-xs text-muted-foreground">
                                {document.type?.toUpperCase()} Document
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-card-foreground text-xs">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1 text-muted-foreground" />
                            {formatDate(document.metadata?.created_at_unix_secs)}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-card-foreground text-xs">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1 text-muted-foreground" />
                            {formatDate(document.metadata?.last_updated_at_unix_secs)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 border-0 hover:border-0 focus:border-0 active:border-0 focus:ring-0 focus:ring-offset-0 outline-none shadow-none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteDocument(document);
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Add URL Dialog */}
      <Dialog open={addUrlDialogOpen} onOpenChange={setAddUrlDialogOpen}>
        <DialogContent className="sm:max-w-[48rem] bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-chart-4/10 rounded-lg flex items-center justify-center">
                <Link className="w-4 h-4 text-chart-4" />
              </div>
              Add URL
            </DialogTitle>
            <DialogDescription>
              Import content from a web page by providing a URL and document name.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="documentName">Document Name</Label>
              <Input
                id="documentName"
                type="text"
                placeholder="Enter document name"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                className={formErrors.documentName ? 'border-destructive' : ''}
              />
              {formErrors.documentName && (
                <p className="text-sm text-destructive">{formErrors.documentName}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="documentUrl">URL</Label>
              <Input
                id="documentUrl"
                type="url"
                placeholder="https://website.com"
                value={documentUrl}
                onChange={(e) => setDocumentUrl(e.target.value)}
                className={formErrors.documentUrl ? 'border-destructive' : ''}
              />
              {formErrors.documentUrl && (
                <p className="text-sm text-destructive">{formErrors.documentUrl}</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={handleAddUrl} disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add URL'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Text Dialog */}
      <Dialog open={addTextDialogOpen} onOpenChange={setAddTextDialogOpen}>
        <DialogContent className="sm:max-w-[48rem] bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-chart-2/10 rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-chart-2" />
              </div>
              Create Text
            </DialogTitle>
            <DialogDescription>
              Add custom text content to your knowledge base by providing a document name and text content.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="textDocumentName">Document Name</Label>
              <Input
                id="textDocumentName"
                type="text"
                placeholder="Enter document name"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                className={formErrors.documentName ? 'border-destructive' : ''}
              />
              {formErrors.documentName && (
                <p className="text-sm text-destructive">{formErrors.documentName}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="documentText">Text Content</Label>
              <Textarea
                id="documentText"
                placeholder="Enter your text content here..."
                value={documentText}
                onChange={(e) => setDocumentText(e.target.value)}
                className={formErrors.documentText ? 'border-destructive' : ''}
                rows={6}
              />
              {formErrors.documentText && (
                <p className="text-sm text-destructive">{formErrors.documentText}</p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={handleAddText} disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Text'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Files Dialog */}
      <Dialog open={addFilesDialogOpen} onOpenChange={setAddFilesDialogOpen}>
        <DialogContent className="sm:max-w-[32rem] bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="w-8 h-8 bg-chart-1/10 rounded-lg flex items-center justify-center">
                <File className="w-4 h-4 text-chart-1" />
              </div>
              Add Files
            </DialogTitle>
            <DialogDescription>
              Upload documents and files to your knowledge base by selecting files.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>File Upload</Label>
              <Dropzone
                onFileSelect={setSelectedFiles}
                onFileRemove={handleRemoveFile}
                selectedFiles={selectedFiles}
                multiple={true}
                maxSize={21 * 1024 * 1024} // 21MB
              />
              {formErrors.selectedFiles && (
                <p className="text-sm text-destructive">{formErrors.selectedFiles}</p>
              )}
            </div>
            
            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div className="space-y-2">
                <Label>Selected Files ({selectedFiles.length})</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg border">
                      <div className="w-8 h-8 bg-chart-1/10 rounded flex items-center justify-center">
                        <File className="w-4 h-4 text-chart-1" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-card-foreground truncate">{file.name}</p>
                        <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(index)}
                        className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <Separator className="my-4" />
          
          <DialogFooter>
            <Button onClick={handleAddFile} disabled={isSubmitting}>
              {isSubmitting ? 'Uploading...' : `Upload ${selectedFiles.length > 0 ? `${selectedFiles.length} File${selectedFiles.length > 1 ? 's' : ''}` : 'Files'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{documentToDelete?.name}"?
            </DialogDescription>
          </DialogHeader>
          <Alert className="mt-4 bg-muted/50 border border-border">
            <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="var(--destructive)">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <AlertTitle className="text-sm font-medium text-destructive">Warning</AlertTitle>
            <AlertDescription className="text-xs leading-relaxed text-destructive">
              The document will be removed from our servers. This action cannot be undone.
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteDocument}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/80 focus:ring-red-600"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default KnowledgeBase;
